import { WishlistLineItem } from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';
import {
  Image,
  LineItemPlaceholder,
  QuantitySelector,
  SmartLink,
  WishlistToggle,
} from '@components';

import { useWishlistItem } from '@hooks';
import { AddShoppingCart } from '@mui/icons-material';
import { Box, IconButton, Stack, styled } from '@mui/material';
import { colors } from '@themes/colors';
import { breakpoints } from '@themes/theme.helpers';
import { t } from '@translations';
import { formatPrice } from '@utils/price.utils';

const ImageWrapper = styled(Box)`
  img {
    height: 100px;
    width: 100px;
  }
`;

const NameWrapper = styled(Box)`
  font-weight: 700;
`;

const WishlistItemWrapper = styled(Box)`
  padding: 16px;
  background: #fff;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  width: 100%;
  ${breakpoints.up('lg')} {
    justify-content: space-between;
    flex-direction: row;
    padding: 16px 0 8px;
  }
`;

const WishlistBox = styled(Box)`
  background-color: #fff;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

export const WishlistCell = styled(Box)`
  display: flex;

  ${breakpoints.up('lg')} {
    align-items: center;
    width: 150px;
    justify-content: center;

    &.bold {
      font-weight: 700;
    }
    &.uppercase {
      text-transform: uppercase;
    }
    &.long {
      flex-grow: 1;
      justify-content: flex-start;
    }

    &.actions {
      width: 80px;
      text-align: right;
    }

    &.actions-with-favorites {
      width: 100px;
      text-align: right;
    }

    &.center {
      justify-content: center;
      text-align: right;
    }
  }
`;

export type WishLineItemProps = {
  isWorking?: boolean;
  lineItem?: WishlistLineItem;
};

export const DesktopWishlistLineItem = (props: WishLineItemProps) => {
  const { lineItem = {}, isWorking } = props;
  const { name, productNo = '', code, url } = lineItem;

  const {
    resizedImageUrl,
    localQuantity,
    setLocalQuantityHandler,
    addToCartHandler,
    itemPrice,
    isAddToCartWorking,
  } = useWishlistItem(lineItem);

  // Placeholder
  if (isWorking) {
    return <LineItemPlaceholder />;
  }

  return (
    <WishlistBox sx={{ display: { xs: 'none', lg: 'block' }, height: '100%' }}>
      <WishlistItemWrapper sx={{ height: '100%' }}>
        <WishlistCell className='center'>
          <ImageWrapper>
            <Image image={resizedImageUrl} />
          </ImageWrapper>
        </WishlistCell>
        <WishlistCell className='long'>
          <Stack direction='column' gap={1}>
            <SmartLink href={url}>
              <NameWrapper>{name}</NameWrapper>
            </SmartLink>
            <Box>{t(TextCodes.Product.Details.ProductNumber, { productNo })}</Box>
          </Stack>
        </WishlistCell>
        <WishlistCell>{formatPrice(itemPrice)}</WishlistCell>

        <WishlistCell className='bold center'>
          <QuantitySelector dark quantity={localQuantity} setQuantity={setLocalQuantityHandler} />
        </WishlistCell>

        <WishlistCell>
          <Box sx={{ display: 'flex', gap: 2, pr: 2, justifyContent: 'flex-end' }}>
            <IconButton
              onClick={addToCartHandler}
              disabled={isAddToCartWorking}
              sx={{ color: colors.primary.mainGreen }}
            >
              <AddShoppingCart />
            </IconButton>
            <WishlistToggle code={code || ''} hasRemoveIcon />
          </Box>
        </WishlistCell>
      </WishlistItemWrapper>
    </WishlistBox>
  );
};
